var __assign = this && this.__assign || function () {
  __assign = Object.assign || function (t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
      s = arguments[i];
      for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
    }
    return t;
  };
  return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Button, Paper, Typography, buttonClasses, styled, useTheme } from '@mui/material';
import { map, times as timesFromLodash } from 'lodash';
import moment from 'moment';
import React, { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import 'swiper/css';
import { Swiper, SwiperSlide, useSwiper } from 'swiper/react';
import { PlanType } from '../../enum';
import { formatDateToLocaleUppercase } from '../../util';
import { MULTISKILL_COLOR } from '../annual/constants';
import { Autocomplete, Table, TableBody, TableCell, TableContainer, TableFooter, TableHead, TableRow } from '../common';
import { QueueType } from '../multiskill/types';
import { useReportAPI } from '../report/selectors';
import { appliesPatienceRetryFields } from '../wizard/workVolume/patienceRetry';
import { DETAIL_LABELS, DETAIL_LABEL_INDEX, MULTISKILL_LABELS, ONE_DECIMAL_DETAIL, PERCENTAGES_DETAIL } from './constants';
import { useDailyContext } from './context';
import { selectDailyDataTransformed, selectTimes, useDailyAPI, useGetQueueNames, usePlanColor, useQueueById, useReportTimelineOptions } from './selectors';
var FOOTER_KEYS = ['total', 'staffHours'];
var TableCellStyled = styled(TableCell)(function (_a) {
  var theme = _a.theme;
  return {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: theme.typography.pxToRem(100)
  };
});
export var Legend = styled(Box)(function (_a) {
  var theme = _a.theme;
  return {
    backgroundColor: theme.palette.grey[400],
    border: "1px solid ".concat(theme.palette.grey[600]),
    padding: theme.spacing(1.5, 6)
  };
});
var TableNav = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  margin: '0 auto'
});
export var ContentNav = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  position: 'relative'
});
var TableNavButton = styled(Button)(function (_a) {
  var _b;
  var theme = _a.theme;
  return _b = {
    paddingTop: theme.typography.pxToRem(10),
    paddingBottom: theme.typography.pxToRem(10)
  }, _b["&.".concat(buttonClasses.root, ".").concat(buttonClasses.disabled)] = {
    borderWidth: 0,
    backgroundColor: 'unset',
    opacity: 0.5
  }, _b;
});
var NavTitle = styled(Typography)(function (_a) {
  var theme = _a.theme;
  return {
    fontWeight: theme.typography.fontWeightBold
  };
});
export var StyledJumpTo = styled(Box)({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  position: 'absolute',
  right: 0
});
export var StyledAutocomplete = styled(Autocomplete)(function (_a) {
  var theme = _a.theme;
  return {
    width: theme.typography.pxToRem(220),
    marginLeft: theme.typography.pxToRem(10)
  };
});
export var TableNavBar = function (_a) {
  var title = _a.title,
    onNext = _a.onNext,
    onPrev = _a.onPrev,
    disabledNext = _a.disabledNext,
    disabledPrev = _a.disabledPrev;
  var theme = useTheme();
  var isPeriodType = true;
  var variantBtn = isPeriodType ? 'text' : 'contained';
  var bgcolor = isPeriodType ? '' : theme.palette.text.primary;
  var color = isPeriodType ? theme.palette.text.primary : theme.palette.common.white;
  var fontSizeTitle = isPeriodType ? theme.typography.pxToRem(18) : theme.typography.pxToRem(16);
  return _jsxs(TableNav, __assign({
    bgcolor: bgcolor
  }, {
    children: [_jsx(TableNavButton, __assign({
      "data-testid": "prev-btn",
      disabled: disabledPrev,
      onClick: function () {
        return onPrev();
      },
      variant: variantBtn,
      disableElevation: true
    }, {
      children: _jsx(FontAwesomeIcon, {
        icon: "chevron-left",
        size: "xl"
      })
    })), _jsx(NavTitle, __assign({
      sx: {
        fontSize: fontSizeTitle
      },
      color: color
    }, {
      children: formatDateToLocaleUppercase(title)
    })), _jsx(TableNavButton, __assign({
      "data-testid": "next-btn",
      disabled: disabledNext,
      onClick: function () {
        return onNext();
      },
      variant: variantBtn,
      disableElevation: true
    }, {
      children: _jsx(FontAwesomeIcon, {
        icon: "chevron-right",
        size: "xl"
      })
    }))]
  }));
};
var DayTableHead = function (_a) {
  var hasMultiskill = _a.hasMultiskill,
    showAbandonmentRate = _a.showAbandonmentRate;
  var t = useTranslation('report', {
    keyPrefix: 'daily'
  }).t;
  var headerColor = usePlanColor()[0];
  var queueNames = useGetQueueNames();
  var singleSkills = queueNames.filter(function (queue) {
    return queue.queueType === QueueType.SingleSkill;
  });
  var visibleDetailLabels = showAbandonmentRate ? DETAIL_LABELS : DETAIL_LABELS.filter(function (label) {
    return label !== 'abandonmentRate';
  });
  var getColSpan = function (title) {
    if (!hasMultiskill) return 2;
    return MULTISKILL_LABELS.includes(title) ? queueNames.length : singleSkills.length;
  };
  return _jsxs(_Fragment, {
    children: [_jsxs(TableRow, {
      children: [_jsx(TableCell, __assign({
        component: "td",
        rowSpan: 2,
        sx: {
          width: '8rem'
        },
        color: headerColor
      }, {
        children: t('interval')
      })), _jsx(TableCell, __assign({
        component: "td",
        rowSpan: hasMultiskill ? 1 : 2,
        colSpan: hasMultiskill ? singleSkills.length : 1,
        color: headerColor
      }, {
        children: t('workVolume')
      })), visibleDetailLabels.filter(function (title) {
        return title !== 'workVolume';
      }).map(function (title) {
        return _jsx(TableCell, __assign({
          component: "td",
          colSpan: getColSpan(title),
          color: headerColor
        }, {
          children: t(title)
        }), title);
      })]
    }), _jsx(TableRow, {
      children: hasMultiskill ? visibleDetailLabels.map(function (label) {
        var queuesForLabel = MULTISKILL_LABELS.includes(label) ? queueNames : singleSkills;
        return queuesForLabel.map(function (queue) {
          return _jsx(TableCellStyled, __assign({
            component: "td",
            title: queue.queueName
          }, {
            children: queue.queueName
          }), "".concat(label, "-").concat(queue.queueName));
        });
      }) : timesFromLodash(visibleDetailLabels.filter(function (l) {
        return l !== 'workVolume';
      }).length).map(function (i) {
        return _jsxs(Fragment, {
          children: [_jsx(TableCell, __assign({
            component: "td"
          }, {
            children: t('model')
          })), _jsx(TableCell, __assign({
            component: "td"
          }, {
            children: t('adjusted')
          }))]
        }, "".concat(visibleDetailLabels[i + 1], "-").concat(i));
      })
    })]
  });
};
var DayTable = function (_a) {
  var _b, _c, _d;
  var time = _a.time,
    index = _a.index,
    data = _a.data,
    showAbandonmentRate = _a.showAbandonmentRate;
  var swiper = useSwiper();
  var _e = usePlanColor(),
    bodyColor = _e[1];
  var _f = useTranslation(['report', 'translation']),
    i18n = _f.i18n,
    t = _f.t;
  var state = useDailyContext()[0];
  var dailyData = useDailyAPI()[0];
  var hasMultiskill = ((_b = state === null || state === void 0 ? void 0 : state.uniqueId) === null || _b === void 0 ? void 0 : _b.includes('-')) && !((_c = dailyData.data) === null || _c === void 0 ? void 0 : _c.detail);
  var queueNames = useGetQueueNames();
  var reportData = useReportAPI()[0].data;
  var isMonthly = (reportData === null || reportData === void 0 ? void 0 : reportData.planType) === PlanType.Monthly || (reportData === null || reportData === void 0 ? void 0 : reportData.planType) === PlanType.MonthlyCustom;
  var dayIndex = Number(index);
  var dayTitle = isMonthly && Number.isFinite(dayIndex) ? formatDateToLocaleUppercase(moment.utc(reportData === null || reportData === void 0 ? void 0 : reportData.startDate).add((_d = state === null || state === void 0 ? void 0 : state.month) !== null && _d !== void 0 ? _d : 0, 'M').add(dayIndex, 'days').locale(i18n.language).format(t('translation:dateFormat.dayMonthYearFormat'))) : t("translation:days.".concat(index));
  var visibleDetailLabels = showAbandonmentRate ? DETAIL_LABELS : DETAIL_LABELS.filter(function (label) {
    return label !== 'abandonmentRate';
  });
  var _g = React.useMemo(function () {
      // Single-skill detail rows are returned as:
      // [workVolume, staffRequired(model), staffRequired(adjusted), serviceLevel(model), serviceLevel(adjusted), asa(model), asa(adjusted), occupancy(model), occupancy(adjusted), abandonment(model), abandonment(adjusted)]
      // If abandonment rate is hidden, we drop the last 2 columns.
      var show = showAbandonmentRate;
      var percentLabels = new Set(['serviceLevel', 'occupancy', 'abandonmentRate']);
      var oneDecimalLabels = new Set(['staffRequired', 'serviceLevel', 'occupancy', 'abandonmentRate']);
      var percentage = [];
      var oneDecimal = [];
      var idx = 1;
      var labels = show ? ['staffRequired', 'serviceLevel', 'asa', 'occupancy', 'abandonmentRate'] : ['staffRequired', 'serviceLevel', 'asa', 'occupancy'];
      for (var _i = 0, labels_1 = labels; _i < labels_1.length; _i++) {
        var label = labels_1[_i];
        var modelIdx = idx;
        var adjIdx = idx + 1;
        if (percentLabels.has(label)) percentage.push(modelIdx, adjIdx);
        if (oneDecimalLabels.has(label)) oneDecimal.push(modelIdx, adjIdx);
        idx += 2;
      }
      return {
        percentageIdx: percentage,
        oneDecimalIdx: oneDecimal,
        visibleLength: show ? 11 : 9
      };
    }, [showAbandonmentRate]),
    percentageIdx = _g.percentageIdx,
    oneDecimalIdx = _g.oneDecimalIdx,
    visibleLength = _g.visibleLength;
  var renderRowEl = function (arrData) {
    return arrData.map(function (key) {
      return _jsxs(TableRow, {
        children: [_jsx(TableCell, __assign({
          component: "th"
        }, {
          children: t(key, {
            defaultValue: key
          })
        })), hasMultiskill ? visibleDetailLabels.map(function (label) {
          var detailIdx = DETAIL_LABEL_INDEX[label];
          var queues = MULTISKILL_LABELS.includes(label) ? queueNames : queueNames.filter(function (queue) {
            return queue.queueType === QueueType.SingleSkill;
          });
          return queues.map(function (_a) {
            var queueName = _a.queueName,
              queueType = _a.queueType;
            // Backend returns "-" for closed intervals, just display the value
            var v = data[key].find(function (item) {
              return item.queueName === queueName && item.queueType === queueType;
            }).detail[detailIdx];
            return _jsx(TableCell, {
              decimal: ONE_DECIMAL_DETAIL.includes(label) ? 1 : 0,
              color: queueType === QueueType.Multiskill ? MULTISKILL_COLOR : '',
              percentage: PERCENTAGES_DETAIL.includes(label),
              value: v
            }, "".concat(queueName, "-").concat(label, "-").concat(detailIdx));
          });
        }) : data[key].slice(0, visibleLength).map(function (v, idx) {
          var decimalPlaces = idx !== 0 && oneDecimalIdx.includes(idx) ? 1 : 0;
          // Backend returns "-" for closed intervals, just display the value
          return _jsx(TableCell, {
            decimal: decimalPlaces,
            color: idx & idx % 2 ? bodyColor : '',
            percentage: percentageIdx.includes(idx),
            value: v,
            component: idx ? 'td' : 'th'
          }, "".concat(key, "-").concat(idx));
        })]
      }, key);
    });
  };
  var bodyRowEl = renderRowEl(time);
  var footerRowEl = renderRowEl(FOOTER_KEYS);
  return _jsxs(_Fragment, {
    children: [_jsx(TableNavBar, {
      onNext: function () {
        return swiper.slideNext();
      },
      onPrev: function () {
        return swiper.slidePrev();
      },
      title: dayTitle
    }), _jsx(TableContainer, __assign({
      maxHeight: "32.425rem",
      sx: {
        width: 'initial'
      }
    }, {
      children: _jsxs(Table, __assign({
        sx: {
          tableLayout: hasMultiskill ? 'auto' : 'fixed'
        }
      }, {
        children: [_jsx(TableHead, {
          children: _jsx(DayTableHead, {
            hasMultiskill: hasMultiskill,
            showAbandonmentRate: showAbandonmentRate
          })
        }), _jsx(TableBody, {
          children: bodyRowEl
        }), _jsx(TableFooter, {
          children: footerRowEl
        })]
      }))
    }))]
  });
};
export var DetailTable = function () {
  var _a, _b, _c;
  var t = useTranslation('report', {
    keyPrefix: 'daily'
  }).t;
  var timelines = useReportTimelineOptions();
  var dailyData = useDailyAPI()[0];
  var _d = useDailyContext('month'),
    _e = _d[0],
    periodIndex = _e === void 0 ? 0 : _e,
    setPeriodIndex = _d[1];
  var times = selectTimes(dailyData.data);
  var slideData = selectDailyDataTransformed(dailyData === null || dailyData === void 0 ? void 0 : dailyData.data);
  var queueId = useDailyContext('queueId')[0];
  var queue = useQueueById(queueId);
  var queueNames = useGetQueueNames();
  var state = useDailyContext()[0];
  var hasMultiskill = ((_a = state === null || state === void 0 ? void 0 : state.uniqueId) === null || _a === void 0 ? void 0 : _a.includes('-')) && !((_b = dailyData.data) === null || _b === void 0 ? void 0 : _b.detail);
  var showAbandonmentRate = React.useMemo(function () {
    if (hasMultiskill) {
      return queueNames === null || queueNames === void 0 ? void 0 : queueNames.some(function (q) {
        return q.isAbandonable;
      });
    }
    return appliesPatienceRetryFields({
      isDeferrable: queue === null || queue === void 0 ? void 0 : queue.isDeferrable,
      worktypeId: queue === null || queue === void 0 ? void 0 : queue.worktypeId,
      metricTarget: queue === null || queue === void 0 ? void 0 : queue.metricTarget
    });
  }, [hasMultiskill, queue, queueNames]);
  return _jsxs(_Fragment, {
    children: [_jsxs(ContentNav, {
      children: [_jsx(TableNavBar, {
        onNext: function () {
          return setPeriodIndex(function (curr) {
            if (curr === void 0) {
              curr = 0;
            }
            return curr + 1;
          });
        },
        onPrev: function () {
          return setPeriodIndex(function (curr) {
            if (curr === void 0) {
              curr = 0;
            }
            return curr - 1;
          });
        },
        title: (_c = timelines[periodIndex]) === null || _c === void 0 ? void 0 : _c.name,
        disabledNext: !!dailyData.requesting || periodIndex === timelines.length - 1,
        disabledPrev: !!dailyData.requesting || !periodIndex
      }), _jsxs(StyledJumpTo, {
        children: [_jsxs(Typography, __assign({
          fontWeight: 600
        }, {
          children: [t('jumpTo'), ":"]
        })), _jsx(StyledAutocomplete, {
          options: timelines,
          value: periodIndex,
          onChange: setPeriodIndex,
          disabled: dailyData.requesting
        })]
      })]
    }), _jsx(Paper, __assign({
      elevation: 0
    }, {
      children: _jsx(Swiper, __assign({
        loop: true
      }, {
        children: map(slideData, function (item, index) {
          return _jsx(SwiperSlide, __assign({
            style: {
              transform: 'none'
            }
          }, {
            children: _jsx(DayTable, {
              index: index,
              time: times !== null && times !== void 0 ? times : [],
              data: item,
              showAbandonmentRate: showAbandonmentRate
            })
          }), index);
        })
      }))
    }))]
  });
};